import React, { useCallback, useEffect, useReducer, useRef } from 'react';
import { useCamera } from '../../hooks/useCamera';
import { useCardDetection } from '../../hooks/useCardDetection';
import { useAutoCapture } from '../../hooks/useAutoCapture';
import { useOcr } from '../../hooks/useOcr';
import { correctPerspective } from '../../services/opencv/perspectiveCorrection';
import { captureVideoFrame, canvasToBlob } from '../../utils/image';
import { CameraView } from '../CameraView';
import { CardOverlay } from '../CardOverlay';
import { CapturePreview } from '../CapturePreview';
import { ResultDisplay } from '../ResultDisplay';
import styles from './CardScanner.module.css';

const initialState = {
  phase: 'scanning',
  currentSide: 'front',
  frontImage: null,
  correctedFrontImage: null,
  backImage: null,
  correctedBackImage: null,
  capturedImage: null,
  correctedImage: null,
  ocrResult: null,
  backOcrResult: null,
  ocrError: null,
};

function scanReducer(state, action) {
  switch (action.type) {
    case 'CAPTURE':
      return {
        ...state,
        phase: 'preview',
        capturedImage: action.capturedImage,
        correctedImage: action.correctedImage,
      };
    case 'CONFIRM_FRONT':
      return {
        ...state,
        phase: 'scanning',
        currentSide: 'back',
        frontImage: state.capturedImage,
        correctedFrontImage: state.correctedImage,
        capturedImage: null,
        correctedImage: null,
      };
    case 'CONFIRM_BACK':
      return {
        ...state,
        backImage: state.capturedImage,
        correctedBackImage: state.correctedImage,
      };
    case 'OCR_SUCCESS':
      return {
        ...state,
        phase: 'result',
        ocrResult: action.front,
        backOcrResult: action.back,
        ocrError: null,
      };
    case 'OCR_ERROR':
      return { ...state, phase: 'result', ocrError: action.error };
    case 'CONFIRM_ID':
      return { ...state, phase: 'result' };
    case 'RETAKE':
      return {
        ...state,
        phase: 'scanning',
        capturedImage: null,
        correctedImage: null,
      };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
}

export function CardScanner({
  mode,
  apiEndpoint,
  onComplete,
  onError,
  facingMode = 'environment',
  stabilityFrames = 10,
  showManualCapture = true,
  className,
}) {
  const [state, dispatch] = useReducer(scanReducer, initialState);
  const overlayRef = useRef(null);
  const isCapturing = useRef(false);

  const camera = useCamera({ facingMode });
  const detection = useCardDetection(camera.videoRef, state.phase === 'scanning' && camera.isReady);
  const autoCapture = useAutoCapture(
    detection.detection.corners,
    detection.detection.areaRatio,
    detection.detection.found,
    { stabilityThreshold: stabilityFrames, enabled: state.phase === 'scanning' }
  );
  const ocr = useOcr(apiEndpoint);

  useEffect(() => {
    camera.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCapture = useCallback(async () => {
    if (isCapturing.current) return;
    isCapturing.current = true;

    try {
      const video = camera.videoRef.current;
      if (!video) return;

      const rawBlob = await captureVideoFrame(video);

      let correctedBlob = rawBlob;
      if (detection.detection.corners) {
        try {
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = video.videoWidth;
          tempCanvas.height = video.videoHeight;
          const ctx = tempCanvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(video, 0, 0);
            const correctedCanvas = correctPerspective(tempCanvas, detection.detection.corners);
            correctedBlob = await canvasToBlob(correctedCanvas);
          }
        } catch (e) {
          // Fall back to raw capture
        }
      }

      dispatch({ type: 'CAPTURE', capturedImage: rawBlob, correctedImage: correctedBlob });
    } catch (err) {
      if (onError) {
        onError(err instanceof Error ? err : new Error('Capture failed'));
      }
    } finally {
      isCapturing.current = false;
    }
  }, [camera.videoRef, detection.detection.corners, onError]);

  autoCapture.onCaptureTrigger(handleCapture);

  const handleConfirm = useCallback(async () => {
    if (state.currentSide === 'front') {
      dispatch({ type: 'CONFIRM_FRONT' });
      return;
    }

    dispatch({ type: 'CONFIRM_BACK' });

    const frontBlob = state.frontImage;
    const backBlob = state.capturedImage;
    if (!frontBlob || !backBlob) return;

    try {
      const result = await ocr.analyzeDual(frontBlob, backBlob);
      dispatch({ type: 'OCR_SUCCESS', front: result.front, back: result.back });
    } catch (err) {
      let errorMsg = 'OCR failed';
      if (err instanceof Error) errorMsg = err.message;
      dispatch({ type: 'OCR_ERROR', error: errorMsg });
    }
  }, [state.currentSide, state.frontImage, state.capturedImage, ocr]);

  const handleDone = useCallback(() => {
    if (state.correctedFrontImage && state.correctedBackImage) {
      let croppedFrontImage;
      if (state.ocrResult?.croppedImage) {
        const byteString = atob(state.ocrResult.croppedImage);
        const bytes = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
        croppedFrontImage = new Blob([bytes], { type: 'image/jpeg' });
      }

      let croppedBackImage;
      if (state.backOcrResult?.croppedImage) {
        const byteString = atob(state.backOcrResult.croppedImage);
        const bytes = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
        croppedBackImage = new Blob([bytes], { type: 'image/jpeg' });
      }

      onComplete({
        frontImage: state.frontImage,
        backImage: state.backImage,
        correctedFrontImage: state.correctedFrontImage,
        correctedBackImage: state.correctedBackImage,
        croppedFrontImage,
        croppedBackImage,
        mode,
        insuranceData: state.ocrResult ?? undefined,
        backInsuranceData: state.backOcrResult ?? undefined,
      });
    }
  }, [state, mode, onComplete]);

  const handleRetake = useCallback(() => {
    ocr.reset();
    dispatch({ type: 'RETAKE' });
  }, [ocr]);

  const handleScanAnother = useCallback(() => {
    ocr.reset();
    dispatch({ type: 'RESET' });
  }, [ocr]);

  if (camera.error) {
    return (
      <div className={`${styles.root} ${className || ''}`}>
        <div className={styles.errorScreen}>
          <p className={styles.errorText}>{camera.error}</p>
          <button className={styles.retryBtn} onClick={() => camera.start()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.root} ${className || ''}`}>
      <div
        className={styles.scannerWrapper}
        style={state.phase !== 'scanning' ? { display: 'none' } : undefined}
      >
        <CameraView videoRef={camera.videoRef} canvasRef={detection.canvasRef} overlayRef={overlayRef} />
        <CardOverlay
          overlayRef={overlayRef}
          videoRef={camera.videoRef}
          corners={detection.detection.corners}
          status={autoCapture.status}
          stableFrames={autoCapture.stableFrames}
          stabilityThreshold={stabilityFrames}
          showManualCapture={showManualCapture}
          onManualCapture={handleCapture}
          currentSide={state.currentSide}
        />
      </div>

      {state.phase === 'preview' && state.correctedImage && (
        <CapturePreview
          imageBlob={state.correctedImage}
          isProcessing={ocr.loading}
          onConfirm={handleConfirm}
          onRetake={handleRetake}
          currentSide={state.currentSide}
        />
      )}

      {state.phase === 'result' && (
        <ResultDisplay
          mode={mode}
          imageBlob={state.correctedFrontImage}
          rawImageBlob={state.frontImage ?? undefined}
          insuranceData={state.ocrResult}
          backImageBlob={state.correctedBackImage ?? undefined}
          backInsuranceData={state.backOcrResult}
          ocrError={state.ocrError}
          onScanAnother={handleScanAnother}
          onDone={handleDone}
        />
      )}
    </div>
  );
}
